import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '../../lib/api.js';
import { MappedDryRun } from './MappedDryRun.js';
import {
  RESOURCE_TYPES,
  TRANSFORMS,
  defFor,
  formToSpec,
  specToForm,
  validateSpec,
  type MapperForm,
  type MappingSpec,
  type FieldAssignment,
} from './fieldCatalog.js';

const COL_CONST = '__const__';

/**
 * Visual column → FHIR-field mapper. Replaces the raw-JSON textarea: pick a
 * resource type, the identity column, and (per target field) a source column +
 * optional transform, or a constant; wire the patient link for dependents.
 * Produces the same FhirMappingSpec the JSON-editor did, with inline validation,
 * a "Suggest from headers" seed, and a JSON escape hatch. Save & accept persists
 * it so start-fhir-import can launch.
 */
export function FhirSpecBuilder({
  sessionId,
  uploadId,
  headers,
  sourceSystem = 'flat-file',
  onSaved,
}: {
  sessionId: string;
  uploadId: string;
  headers: string[];
  sourceSystem?: string;
  onSaved?: (specId: string) => void;
}) {
  const [form, setForm] = useState<MapperForm>(() => ({
    resourceType: 'Patient',
    sourceSystem,
    identity: '',
    assignments: {},
    referenceColumn: '',
  }));
  const [jsonMode, setJsonMode] = useState(false);
  const [rawJson, setRawJson] = useState('');

  const def = defFor(form.resourceType);

  const spec: MappingSpec | null = useMemo(() => {
    if (jsonMode) {
      try {
        return JSON.parse(rawJson) as MappingSpec;
      } catch {
        return null;
      }
    }
    return formToSpec(form);
  }, [jsonMode, rawJson, form]);

  const errors = useMemo(() => (spec ? validateSpec(spec, headers) : ['Editor contains invalid JSON.']), [spec, headers]);

  const setField = (path: string, patch: Partial<FieldAssignment>) =>
    setForm((f) => ({ ...f, assignments: { ...f.assignments, [path]: { ...f.assignments[path], ...patch } } }));
  const clearField = (path: string) =>
    setForm((f) => {
      const a = { ...f.assignments };
      delete a[path];
      return { ...f, assignments: a };
    });

  const hydrate = (s: MappingSpec) => {
    setForm(specToForm(s));
    setJsonMode(false);
  };

  const suggest = useMutation({
    // AI-assisted when a model is configured; falls back to the deterministic
    // header heuristic otherwise (so it always returns a usable spec).
    mutationFn: () =>
      api.aiSuggestFhirSpec(sessionId, headers, { resourceType: form.resourceType, sourceSystem: form.sourceSystem }),
    onSuccess: (res) => {
      hydrate(res.spec as unknown as MappingSpec);
      toast.success(res.source === 'ai' ? 'Mapping suggested by AI — review & adjust' : 'Mapping suggested from headers — review & adjust');
    },
    onError: (e) => toast.error(`Suggest failed: ${String(e)}`),
  });

  const save = useMutation({
    mutationFn: async () => {
      if (!spec) throw new Error('Editor contains invalid JSON');
      if (errors.length) throw new Error(errors[0] ?? 'spec is invalid');
      const { specId } = await api.createFhirMappingSpec(sessionId, spec as unknown as Record<string, unknown>, { authoredBy: 'human' });
      await api.acceptFhirMappingSpec(sessionId, specId);
      return specId;
    },
    onSuccess: (specId) => {
      toast.success('Spec saved & accepted');
      onSaved?.(specId);
    },
    onError: (e) => toast.error(`Save failed: ${String(e)}`),
  });

  const toggleJson = () => {
    if (!jsonMode) {
      setRawJson(JSON.stringify(formToSpec(form), null, 2));
      setJsonMode(true);
    } else {
      try {
        hydrate(JSON.parse(rawJson) as MappingSpec);
      } catch (e) {
        toast.error(`Can’t parse JSON: ${String(e)}`);
        return;
      }
    }
  };

  const ctl = { background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-primary)' };
  const colValue = (a: FieldAssignment | undefined): string => (a?.const != null ? COL_CONST : (a?.column ?? ''));

  return (
    <div className="space-y-3 text-[12px]">
      {/* top controls */}
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1">
          <span style={{ color: 'var(--text-dimmed)' }}>Resource</span>
          <select
            className="rounded px-1.5 py-1"
            style={ctl}
            value={form.resourceType}
            onChange={(e) => setForm((f) => ({ ...f, resourceType: e.target.value, assignments: {}, referenceColumn: '' }))}
          >
            {RESOURCE_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1">
          <span style={{ color: 'var(--text-dimmed)' }}>Source</span>
          <input
            className="rounded px-1.5 py-1 w-32"
            style={ctl}
            value={form.sourceSystem}
            onChange={(e) => setForm((f) => ({ ...f, sourceSystem: e.target.value }))}
          />
        </label>
        <label className="flex items-center gap-1">
          <span style={{ color: 'var(--text-dimmed)' }}>Identity</span>
          <select className="rounded px-1.5 py-1" style={ctl} value={form.identity} onChange={(e) => setForm((f) => ({ ...f, identity: e.target.value }))}>
            <option value="">— pick id column —</option>
            {headers.map((h) => (
              <option key={h} value={h}>{h}</option>
            ))}
          </select>
        </label>
        <button
          className="rounded px-2 py-1"
          style={{ ...ctl, opacity: suggest.isPending ? 0.6 : 1 }}
          disabled={suggest.isPending}
          onClick={() => suggest.mutate()}
        >
          {suggest.isPending ? 'Suggesting…' : 'Suggest from headers'}
        </button>
        <button className="rounded px-2 py-1" style={ctl} onClick={toggleJson}>
          {jsonMode ? 'Visual editor' : 'Edit JSON'}
        </button>
      </div>

      {jsonMode ? (
        <textarea
          className="w-full rounded p-2 font-mono text-[11px]"
          style={{ ...ctl, minHeight: 240 }}
          value={rawJson}
          onChange={(e) => setRawJson(e.target.value)}
          spellCheck={false}
        />
      ) : (
        <div className="space-y-1">
          {/* patient-link reference (dependents) */}
          {def?.referenceKey ? (
            <div className="flex items-center gap-2 rounded px-2 py-1.5" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)' }}>
              <span className="w-40 shrink-0" style={{ color: 'var(--accent-blue, var(--text-primary))' }}>Patient link (MRN) *</span>
              <select className="rounded px-1.5 py-1 flex-1" style={ctl} value={form.referenceColumn} onChange={(e) => setForm((f) => ({ ...f, referenceColumn: e.target.value }))}>
                <option value="">— pick the patient-id column —</option>
                {headers.map((h) => (
                  <option key={h} value={h}>{h}</option>
                ))}
              </select>
            </div>
          ) : null}

          {/* target fields */}
          {(def?.fields ?? []).map((tf) => {
            const a = form.assignments[tf.path];
            const cv = colValue(a);
            return (
              <div key={tf.path} className="flex items-center gap-2 rounded px-2 py-1" style={{ borderBottom: '1px solid var(--border)' }}>
                <span className="w-40 shrink-0" style={{ color: 'var(--text-primary)' }}>
                  {tf.label}
                  {tf.required ? <span style={{ color: 'var(--accent-red)' }}> *</span> : null}
                </span>
                <select
                  className="rounded px-1.5 py-1 w-44"
                  style={ctl}
                  value={cv}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '') clearField(tf.path);
                    else if (v === COL_CONST) setField(tf.path, { column: undefined, transform: undefined, const: a?.const ?? '' });
                    else setField(tf.path, { column: v, const: undefined, transform: a?.transform ?? tf.suggestTransform });
                  }}
                >
                  <option value="">— unmapped —</option>
                  {headers.map((h) => (
                    <option key={h} value={h}>{h}</option>
                  ))}
                  <option value={COL_CONST}>(constant…)</option>
                </select>
                {a?.const != null ? (
                  <input className="rounded px-1.5 py-1 flex-1" style={ctl} placeholder="constant value" value={a.const} onChange={(e) => setField(tf.path, { const: e.target.value })} />
                ) : a?.column ? (
                  <select className="rounded px-1.5 py-1 flex-1" style={ctl} value={a.transform ?? ''} onChange={(e) => setField(tf.path, { transform: e.target.value || undefined })}>
                    {TRANSFORMS.map((t) => (
                      <option key={t.name} value={t.name}>{t.desc}</option>
                    ))}
                  </select>
                ) : (
                  <span className="flex-1" style={{ color: 'var(--text-dimmed)' }} />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* validation + save */}
      {errors.length > 0 ? (
        <ul className="space-y-0.5 text-[11px]" style={{ color: 'var(--accent-red)' }}>
          {errors.map((e, i) => (
            <li key={i}>• {e}</li>
          ))}
        </ul>
      ) : (
        <div className="text-[11px]" style={{ color: 'var(--accent-green, var(--text-dimmed))' }}>✓ Spec looks valid</div>
      )}
      <button
        className="rounded px-3 py-1.5 font-medium"
        style={{
          background: errors.length || save.isPending ? 'var(--bg-tertiary)' : 'var(--accent-blue, var(--text-primary))',
          color: errors.length || save.isPending ? 'var(--text-dimmed)' : 'var(--bg-primary, #fff)',
          border: '1px solid var(--border)',
        }}
        disabled={errors.length > 0 || save.isPending}
        onClick={() => save.mutate()}
      >
        {save.isPending ? 'Saving…' : 'Save & accept'}
      </button>

      {spec && uploadId ? (
        <div className="pt-2" style={{ borderTop: '1px solid var(--border)' }}>
          <div className="mb-1 text-[10px] uppercase tracking-widest" style={{ color: 'var(--text-dimmed)' }}>Mapping preview</div>
          <MappedDryRun sessionId={sessionId} uploadId={uploadId} spec={spec as unknown as Record<string, unknown>} />
        </div>
      ) : null}
    </div>
  );
}
